import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../lib/axios';
import { dateFmt } from '../lib/format';
import { useIsTenantAdmin, useIsSuperAdmin } from '../stores/authStore';
import { useTenantLogo } from '../lib/useTenantLogo';
import { useModuleVisibility } from '../lib/useModuleVisibility';
import TeamMembersCard from '../components/TeamMembersCard';
import CollapsibleCard from '../components/CollapsibleCard';
import ChartOfAccounts from './ChartOfAccounts';

type Settings = {
  id?: number;
  organizationName?: string;
  siret?: string;
  address?: string;
  contactEmail?: string;
  currency?: string;
  vatEnabled?: boolean;
  cashEnabled?: boolean;
  dashboardMonthMode?: string;
  hijriCalendarEnabled?: boolean;
};

type FiscalYear = {
  id: number;
  year: number;
  label?: string;
  startDate: string;
  closingDate: string;
  status: string;
};

type TreasuryAccount = {
  id: number;
  kind: 'BANK' | 'CASH';
  name: string;
  accountNumber: string;
  iban?: string | null;
  bic?: string | null;
  bankName?: string | null;
  defaultAccount: boolean;
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function Settings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isTenantAdmin = useIsTenantAdmin();
  const { isHidden } = useModuleVisibility();
  const [tab, setTab] = useState<'general' | 'accounts'>('general');

  const settings = useQuery<Settings>({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then((r) => r.data),
  });

  const apiKeys = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get('/api-keys').then((r) => r.data),
  });

  const fiscalYears = useQuery<FiscalYear[]>({
    queryKey: ['fiscal-years'],
    queryFn: () => api.get('/fiscal-years').then((r) => r.data),
  });

  const [form, setForm] = useState<Settings>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => api.put('/settings', form).then((r) => r.data),
    onSuccess: () => {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const set = (patch: Partial<Settings>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div>
      <header className="mb-6">
        <p className="eyebrow">{t('nav.settings')}</p>
        <h1 className="page-title mt-1">{t('settings.title')}</h1>
      </header>

      <div className="mb-6 flex gap-1 border-b border-line">
        <button
          type="button"
          onClick={() => setTab('general')}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'general'
              ? 'border-brand text-brand'
              : 'border-transparent text-ink-muted hover:text-ink'
          }`}
        >
          {t('settings.tabGeneral')}
        </button>
        <button
          type="button"
          onClick={() => setTab('accounts')}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'accounts'
              ? 'border-brand text-brand'
              : 'border-transparent text-ink-muted hover:text-ink'
          }`}
        >
          {t('nav.chartOfAccounts')}
        </button>
      </div>

      {tab === 'accounts' ? (
        <ChartOfAccounts embedded />
      ) : (
        <>
          <CollapsibleCard title={t('settings.organization')} className="mb-6 max-w-lg">
            <div className="space-y-4">
              <div>
                <label className="field-label">{t('settings.name')}</label>
                <input
                  className="input w-full"
                  value={form.organizationName ?? ''}
                  onChange={(e) => set({ organizationName: e.target.value })}
                />
              </div>
              <div>
                <label className="field-label">{t('settings.siret')}</label>
                <input
                  className="input w-full tnum"
                  value={form.siret ?? ''}
                  onChange={(e) => set({ siret: e.target.value })}
                />
              </div>
              <div>
                <label className="field-label">{t('settings.address')}</label>
                <input
                  className="input w-full"
                  value={form.address ?? ''}
                  onChange={(e) => set({ address: e.target.value })}
                />
              </div>
              <div>
                <label className="field-label">{t('settings.contactEmail')}</label>
                <input
                  type="email"
                  className="input w-full"
                  value={form.contactEmail ?? ''}
                  onChange={(e) => set({ contactEmail: e.target.value })}
                />
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="field-label">{t('settings.currency')}</label>
                  <input
                    className="input w-full"
                    value={form.currency ?? 'EUR'}
                    onChange={(e) => set({ currency: e.target.value })}
                  />
                </div>
                <label className="flex items-end gap-2 pb-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!form.vatEnabled}
                    onChange={(e) => set({ vatEnabled: e.target.checked })}
                  />
                  {t('settings.vatEnabled')}
                </label>
              </div>
              <div className="border-t border-line pt-4">
                <label className="field-label">{t('settings.dashboardMonth')}</label>
                <select
                  className="input w-full"
                  value={form.dashboardMonthMode ?? 'CURRENT'}
                  onChange={(e) => set({ dashboardMonthMode: e.target.value })}
                >
                  <option value="CURRENT">{t('settings.dashboardMonthCurrent')}</option>
                  <option value="PREVIOUS">{t('settings.dashboardMonthPrevious')}</option>
                </select>
                <p className="mt-1 text-xs text-ink-muted">{t('settings.dashboardMonthHint')}</p>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!form.hijriCalendarEnabled}
                  onChange={(e) => set({ hijriCalendarEnabled: e.target.checked })}
                />
                <span>
                  {t('settings.hijriCalendar')}
                  <span className="block text-xs text-ink-muted">{t('settings.hijriCalendarHint')}</span>
                </span>
              </label>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={() => save.mutate()}
                disabled={save.isPending}
                className="btn btn-primary"
              >
                {t('common.save')}
              </button>
              {saved && (
                <span className="text-sm font-medium text-income">{t('settings.saved')}</span>
              )}
            </div>
          </CollapsibleCard>

          {isTenantAdmin && <LogoCard />}

          <TreasuryAccountsCard />

          <FiscalYearsCard fiscalYears={fiscalYears.data ?? []} />

          {isTenantAdmin && <TeamMembersCard />}

          {!isHidden('api') && (
          <CollapsibleCard title={t('settings.apiKeys')} className="max-w-2xl">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('field.label')}</th>
                    <th>{t('field.prefix')}</th>
                    <th>{t('field.active')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(apiKeys.data ?? []).map((k: any) => (
                    <tr key={k.id}>
                      <td data-label={t('field.label')} className="font-medium">
                        {k.label}
                      </td>
                      <td data-label={t('field.prefix')} className="tnum text-ink-soft">
                        {k.keyPrefix}…
                      </td>
                      <td data-label={t('field.active')}>
                        {k.active ? t('common.yes') : t('common.no')}
                      </td>
                    </tr>
                  ))}
                  {(apiKeys.data ?? []).length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-ink-muted">
                        {t('settings.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleCard>
          )}
        </>
      )}
    </div>
  );
}

function LogoCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: logoUrl } = useTenantLogo();
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tenant-logo'] });
    qc.invalidateQueries({ queryKey: ['settings'] });
  };

  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api
        .post('/settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((r) => r.data);
    },
    onSuccess: invalidate,
    onError: (e: any) =>
      setError(e?.response?.data?.message ?? t('settings.logoError')),
  });

  const remove = useMutation({
    mutationFn: () => api.delete('/settings/logo').then((r) => r.data),
    onSuccess: invalidate,
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    upload.mutate(file);
  };

  return (
    <CollapsibleCard title={t('settings.logo')} className="mb-6 max-w-lg">
      <div className="flex items-center gap-5">
        <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-xl border border-dashed border-line bg-surface-raised">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-full w-full object-contain" />
          ) : (
            <span className="text-xs text-ink-muted">{t('settings.logoNone')}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ink-soft">{t('settings.logoHint')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="btn btn-secondary cursor-pointer">
              {upload.isPending ? t('common.loading') : t('settings.logoUpload')}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif"
                onChange={onPick}
                className="hidden"
              />
            </label>
            {logoUrl && (
              <button
                type="button"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
                className="btn btn-ghost text-expense"
              >
                {t('settings.logoRemove')}
              </button>
            )}
          </div>
          {error && <p className="mt-2 text-sm text-expense">{error}</p>}
        </div>
      </div>
    </CollapsibleCard>
  );
}

function TreasuryAccountsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<'BANK' | 'CASH'>('BANK');
  const [name, setName] = useState('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [bankName, setBankName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery<TreasuryAccount[]>({
    queryKey: ['treasury-accounts'],
    queryFn: () => api.get('/treasury-accounts').then((r) => r.data),
  });

  const reset = () => {
    setName('');
    setIban('');
    setBic('');
    setBankName('');
    setError(null);
  };

  const create = useMutation({
    mutationFn: () =>
      api
        .post('/treasury-accounts', {
          kind,
          name,
          iban: kind === 'BANK' ? iban || null : null,
          bic: kind === 'BANK' ? bic || null : null,
          bankName: kind === 'BANK' ? bankName || null : null,
        })
        .then((r) => r.data),
    onSuccess: () => {
      reset();
      qc.invalidateQueries({ queryKey: ['treasury-accounts'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('treasuryAccounts.error')),
  });

  const setDefault = useMutation({
    mutationFn: (id: number) => api.post(`/treasury-accounts/${id}/default`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['treasury-accounts'] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/treasury-accounts/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['treasury-accounts'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('treasuryAccounts.error')),
  });

  const list = accounts.data ?? [];

  return (
    <CollapsibleCard title={t('treasuryAccounts.title')} className="mb-6 max-w-2xl">
      <p className="pb-3 text-sm text-ink-muted">{t('treasuryAccounts.intro')}</p>

      <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg bg-surface-raised p-3">
        <div>
          <label className="field-label">{t('treasuryAccounts.kind')}</label>
          <select
            className="input"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'BANK' | 'CASH')}
          >
            <option value="BANK">{t('treasuryAccounts.bank')}</option>
            <option value="CASH">{t('treasuryAccounts.cash')}</option>
          </select>
        </div>
        <div className="flex-1 min-w-[10rem]">
          <label className="field-label">{t('treasuryAccounts.name')}</label>
          <input className="input w-full" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        {kind === 'BANK' && (
          <>
            <div className="min-w-[12rem] flex-1">
              <label className="field-label">{t('treasuryAccounts.iban')}</label>
              <input className="input w-full tnum" value={iban} onChange={(e) => setIban(e.target.value)} />
            </div>
            <div>
              <label className="field-label">{t('treasuryAccounts.bic')}</label>
              <input className="input w-28 tnum" value={bic} onChange={(e) => setBic(e.target.value)} />
            </div>
            <div className="min-w-[8rem] flex-1">
              <label className="field-label">{t('treasuryAccounts.bankName')}</label>
              <input
                className="input w-full"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
              />
            </div>
          </>
        )}
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending || !name.trim()}
          className="btn btn-primary"
        >
          {t('common.add')}
        </button>
        {error && <span className="w-full text-sm font-medium text-expense">{error}</span>}
      </div>

      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t('treasuryAccounts.kind')}</th>
              <th>{t('treasuryAccounts.name')}</th>
              <th>{t('treasuryAccounts.account')}</th>
              <th>{t('treasuryAccounts.iban')}</th>
              <th>{t('treasuryAccounts.default')}</th>
              <th className="text-right">{t('field.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((a) => (
              <tr key={a.id}>
                <td data-label={t('treasuryAccounts.kind')}>
                  {a.kind === 'BANK' ? t('treasuryAccounts.bank') : t('treasuryAccounts.cash')}
                </td>
                <td data-label={t('treasuryAccounts.name')} className="font-medium">
                  {a.name}
                </td>
                <td data-label={t('treasuryAccounts.account')} className="tnum text-ink-soft">
                  {a.accountNumber}
                </td>
                <td data-label={t('treasuryAccounts.iban')} className="tnum text-ink-soft">
                  {a.iban ?? '-'}
                </td>
                <td data-label={t('treasuryAccounts.default')}>
                  {a.defaultAccount ? (
                    <span className="pill pill-posted">{t('treasuryAccounts.default')}</span>
                  ) : (
                    <button
                      onClick={() => setDefault.mutate(a.id)}
                      className="btn btn-ghost !px-2 !py-1 text-xs"
                    >
                      {t('treasuryAccounts.setDefault')}
                    </button>
                  )}
                </td>
                <td className="cell-actions text-right">
                  <button
                    onClick={() => {
                      if (window.confirm(t('treasuryAccounts.confirmDelete'))) remove.mutate(a.id);
                    }}
                    className="btn btn-ghost !px-2 !py-1 text-sm text-expense"
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-ink-muted">
                  {t('treasuryAccounts.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </CollapsibleCard>
  );
}

function FiscalYearsCard({ fiscalYears }: { fiscalYears: FiscalYear[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isSuperAdmin = useIsSuperAdmin();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [label, setLabel] = useState('');
  const [startDate, setStartDate] = useState(today());
  const [closingDate, setClosingDate] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const [balances, setBalances] = useState<Record<string, string>>({});

  const isFirstYear = fiscalYears.length === 0;
  const isEditing = editId != null;
  // Opening balances are editable on the first year (creation) and on any year a super admin edits.
  const showBalances = (isFirstYear && !isEditing) || (isEditing && isSuperAdmin);

  const treasuryAccounts = useQuery<TreasuryAccount[]>({
    queryKey: ['treasury-accounts'],
    queryFn: () => api.get('/treasury-accounts').then((r) => r.data),
    enabled: isFirstYear || isEditing,
  });

  const accounts = treasuryAccounts.data ?? [];
  const hasBank = accounts.some((a) => a.kind === 'BANK');
  const hasCash = accounts.some((a) => a.kind === 'CASH');
  const accountsReady = !isFirstYear || isEditing || (hasBank && hasCash);

  const resetForm = () => {
    setOpen(false);
    setEditId(null);
    setLabel('');
    setBalances({});
    setError(null);
  };

  const startCreate = () => {
    setEditId(null);
    setYear(new Date().getFullYear());
    setLabel('');
    setStartDate(today());
    setClosingDate(today());
    setBalances({});
    setError(null);
    setOpen(true);
  };

  const startEdit = async (fy: FiscalYear) => {
    setEditId(fy.id);
    setYear(fy.year);
    setLabel(fy.label ?? '');
    setStartDate(fy.startDate);
    setClosingDate(fy.closingDate);
    setBalances({});
    setError(null);
    setOpen(true);
    try {
      const rows = await api
        .get<{ accountNumber: string; balance: number | string }[]>(
          `/fiscal-years/${fy.id}/opening-balances`,
        )
        .then((r) => r.data);
      const map: Record<string, string> = {};
      rows.forEach((row) => {
        map[row.accountNumber] = String(row.balance);
      });
      setBalances(map);
    } catch {
      /* no à-nouveaux yet for this year */
    }
  };

  const buildOpeningBalances = () =>
    accounts.map((a) => ({
      accountNumber: a.accountNumber,
      balance: Number(balances[a.accountNumber] || 0),
    }));

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        year,
        label: label || null,
        startDate,
        closingDate,
        openingBalances: showBalances ? buildOpeningBalances() : null,
      };
      return isEditing
        ? api.put(`/fiscal-years/${editId}`, payload).then((r) => r.data)
        : api.post('/fiscal-years', payload).then((r) => r.data);
    },
    onSuccess: () => {
      resetForm();
      qc.invalidateQueries({ queryKey: ['fiscal-years'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('fiscalYears.error')),
  });

  const close = useMutation({
    mutationFn: (id: number) => api.post(`/fiscal-years/${id}/close`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fiscal-years'] }),
  });

  return (
    <CollapsibleCard title={t('fiscalYears.title')} className="mb-6 max-w-2xl">
      <div className="mb-2 flex justify-end">
        <button
          onClick={() => (open ? resetForm() : startCreate())}
          className="btn btn-ghost !px-3 !py-1 text-sm"
        >
          {open ? t('common.cancel') : t('fiscalYears.add')}
        </button>
      </div>

      {open && (
        <div className="mb-3 rounded-lg bg-surface-raised p-3">
          {isEditing && (
            <p className="mb-3 text-sm font-medium text-ink">{t('fiscalYears.editTitle')}</p>
          )}
          {isFirstYear && !isEditing && !accountsReady && (
            <p className="mb-3 rounded-md bg-expense-tint px-3 py-2 text-sm text-expense">
              {t('fiscalYears.needAccounts')}
            </p>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="field-label">{t('fiscalYears.year')}</label>
              <input
                type="number"
                className="input tnum w-24"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </div>
            <div className="flex-1 min-w-[8rem]">
              <label className="field-label">{t('field.label')}</label>
              <input className="input w-full" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div>
              <label className="field-label">{t('fiscalYears.start')}</label>
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="field-label">{t('fiscalYears.closing')}</label>
              <input
                type="date"
                className="input"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
              />
            </div>
          </div>

          {showBalances && accountsReady && (
            <div className="mt-3 border-t border-line pt-3">
              <p className="field-label mb-2">{t('fiscalYears.openingBalances')}</p>
              {accounts.length === 0 ? (
                <p className="text-sm text-ink-muted">{t('fiscalYears.noTreasury')}</p>
              ) : (
                <div className="space-y-2">
                  {accounts.map((a) => (
                    <div key={a.id} className="flex items-center gap-3">
                      <span className="flex-1 text-sm">
                        {a.name}
                        <span className="ml-2 tnum text-xs text-ink-muted">{a.accountNumber}</span>
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        className="input tnum w-36"
                        placeholder="0.00"
                        value={balances[a.accountNumber] ?? ''}
                        onChange={(e) =>
                          setBalances((b) => ({ ...b, [a.accountNumber]: e.target.value }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending || !accountsReady}
              className="btn btn-primary"
            >
              {isEditing ? t('common.save') : t('common.add')}
            </button>
            {error && <span className="text-sm font-medium text-expense">{error}</span>}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t('fiscalYears.year')}</th>
              <th>{t('field.label')}</th>
              <th>{t('fiscalYears.start')}</th>
              <th>{t('fiscalYears.closing')}</th>
              <th>{t('field.status')}</th>
              <th className="text-right">{t('field.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {fiscalYears.map((fy) => (
              <tr key={fy.id}>
                <td data-label={t('fiscalYears.year')} className="tnum font-medium">{fy.year}</td>
                <td data-label={t('field.label')}>{fy.label ?? '-'}</td>
                <td data-label={t('fiscalYears.start')} className="text-ink-soft">{dateFmt(fy.startDate)}</td>
                <td data-label={t('fiscalYears.closing')} className="text-ink-soft">{dateFmt(fy.closingDate)}</td>
                <td data-label={t('field.status')}>
                  <span className={fy.status === 'OPEN' ? 'pill pill-draft' : 'pill pill-posted'}>
                    {t(`fiscalYears.status.${fy.status}`, { defaultValue: fy.status })}
                  </span>
                </td>
                <td className="cell-actions text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isSuperAdmin && (
                      <button
                        onClick={() => startEdit(fy)}
                        className="btn btn-ghost !px-2 !py-1 text-sm"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                    {fy.status === 'OPEN' && (
                      <button
                        onClick={() => {
                          if (window.confirm(t('fiscalYears.confirmClose'))) close.mutate(fy.id);
                        }}
                        className="btn btn-ghost !px-2 !py-1 text-sm"
                      >
                        {t('fiscalYears.close')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {fiscalYears.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-ink-muted">
                  {t('fiscalYears.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </CollapsibleCard>
  );
}
